import { onRole, type Block, type ImageRef, type Link, type Text } from '@ksa/model'
import { SiteImage, SiteLink, SiteText, hasText } from '../context.tsx'

type Props = {
  heading: Text
  subheading: Text
  image: ImageRef
  ctaLabel: Text
  ctaLink: Link
  background: string
}

/**
 * Four structural variants over one component. This is where the creative range comes from without a
 * second stylesheet per template — a template changes tokens, a variant changes arrangement, and
 * neither one has to know about the other.
 *
 * ⚠️ `onRole(background)` DECIDES THE TEXT COLOUR — never a ternary on `'brand'`. In the predecessor
 * this line read `background === 'brand' ? on-brand : on-surface`, which silently gave `on-surface`
 * to every other ground, including grounds that *were* `on-surface`. That was 1.00:1 — the same
 * colour as the text — one dropdown click away, on the first block of the first page of every site.
 */
export function Hero({ block }: { block: Block }) {
  const p = block.props as unknown as Props
  const variant = block.variant ?? 'split'
  const ground = p.background ?? 'brand'
  const style = { background: `var(--${ground})`, color: `var(--${onRole(ground)})` }

  const copy = (
    <div className={variant === 'centered' ? 's-center' : 's-start'}>
      <h1 className="s-h1">
        <SiteText value={p.heading} />
      </h1>
      {hasText(p.subheading) && (
        <p className="s-lead s-soft" style={{ marginBlockStart: '1rem' }}>
          <SiteText value={p.subheading} />
        </p>
      )}
      {hasText(p.ctaLabel) && (
        <div style={{ marginBlockStart: '1.75rem' }}>
          <SiteLink link={p.ctaLink} className="s-btn">
            <SiteText value={p.ctaLabel} />
          </SiteLink>
        </div>
      )}
    </div>
  )

  if (variant === 'minimal') {
    return (
      <section className="s-hero s-section" style={style}>
        <div className="s-wrap">{copy}</div>
      </section>
    )
  }

  if (variant === 'overlay') {
    return (
      <section className="s-hero s-section" style={style}>
        <div className="s-hero__overlay-image">
          <SiteImage image={p.image} />
        </div>
        <div className="s-wrap" style={{ position: 'relative' }}>
          <div className="s-narrow s-center">{copy}</div>
        </div>
      </section>
    )
  }

  if (variant === 'centered') {
    return (
      <section className="s-hero s-section" style={style}>
        <div className="s-wrap s-stack-lg">
          <div className="s-narrow">{copy}</div>
          <div className="s-narrow">
            <SiteImage image={p.image} />
          </div>
        </div>
      </section>
    )
  }

  // split — copy at the inline start, image at the inline end. A two-column grid mirrors under RTL
  // with no second rule, which is why this is a grid rather than two floats.
  return (
    <section className="s-hero s-section" style={style}>
      <div className="s-wrap s-hero__split">
        {copy}
        <SiteImage image={p.image} />
      </div>
    </section>
  )
}
